import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';
import { AppBreadcrumbService } from 'src/app/app.breadcrumb.service';
import { Contacto } from 'src/app/models/notificacion/contacto.model';
import {
    getWhatsappConversationDerivedStatusDetail,
    getWhatsappConversationUiStatus,
    WhatsappAssignableUser,
    WhatsappConversationDetailResponse,
    WhatsappConversationReplyTemplate,
    WhatsappConversationSummary,
    WHATSAPP_CONVERSATION_ACCESS,
    WHATSAPP_CONVERSATION_SPECIAL_PERMISSION,
} from 'src/app/models/notificacion/whatsapp_conversation.model';
import { IPasoRegistroLectura } from 'src/app/models/notificacion/whatsapp_flujo.model';
import { WhatsappConversationService } from 'src/app/services/notificacion/whatsapp_conversation.service';

@Component({
    selector: 'app-whatsapp-conversation-ventana',
    templateUrl: './whatsapp-conversation-ventana.component.html',
    styleUrls: ['./whatsapp-conversation-ventana.component.scss']
})
export class WhatsappConversationVentanaComponent implements OnInit, OnDestroy {

    loading = false;
    loadingUsers = false;
    loadingTemplates = false;
    canCreateContact = false;
    conversationDetail: WhatsappConversationDetailResponse | null = null;
    assignableUsers: WhatsappAssignableUser[] = [];
    availableReplyTemplates: WhatsappConversationReplyTemplate[] = [];
    loadingFlujoRegistro = false;
    flujoRegistro: IPasoRegistroLectura[] = [];

    private routeSubscription?: Subscription;

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private messageService: MessageService,
        private breadcrumbService: AppBreadcrumbService,
        private service: WhatsappConversationService,
    ) {
        this.breadcrumbService.setItems([
            { label: 'Notificacion' },
            { label: 'Inbox WhatsApp', routerLink: '/pages/notificacion/whatsapp-inbox' },
            { label: 'Detalle' },
        ]);
    }

    ngOnInit(): void {
        void this.loadContactCreationAccess();
        this.routeSubscription = this.activatedRoute.paramMap.subscribe(async (params) => {
            const id = Number(params.get('id') || 0);
            if (!id) {
                this.router.navigate(['pages', 'notificacion', 'whatsapp-inbox']);
                return;
            }

            await this.loadConversation(id);
        });
    }

    ngOnDestroy(): void {
        this.routeSubscription?.unsubscribe();
    }

    get conversation(): WhatsappConversationSummary | null {
        return this.conversationDetail?.conversation || null;
    }

    get conversationHeadingName(): string {
        if (!this.conversation) {
            return '';
        }

        return this.resolveConversationHeadingName(this.conversation);
    }

    get showCreateContactAction(): boolean {
        return Boolean(this.conversation)
            && this.canCreateContact
            && !this.hasExistingContact(this.conversation);
    }

    get canManageAssignment(): boolean {
        return this.canEditInbox
            && (
                this.service.seguridadService.esSuperusuario()
            || this.service.seguridadService.tienePermisoEspecialSync(
                WHATSAPP_CONVERSATION_SPECIAL_PERMISSION.MANAGE_ASSIGNMENT.codigo,
                )
            );
    }

    get canViewInbox(): boolean {
        return this.service.seguridadService.puedeVerSync(
            WHATSAPP_CONVERSATION_ACCESS.CODIGO_MODULO,
            WHATSAPP_CONVERSATION_ACCESS.CODIGO_REGISTRO,
        );
    }

    get canEditInbox(): boolean {
        return this.service.seguridadService.puedeEditarSync(
            WHATSAPP_CONVERSATION_ACCESS.CODIGO_MODULO,
            WHATSAPP_CONVERSATION_ACCESS.CODIGO_REGISTRO,
        );
    }

    async loadConversation(id?: number): Promise<void> {
        if (!this.canViewInbox) {
            this.messageService.add({ severity: 'warn', summary: 'Acceso denegado', detail: 'Tu grupo de acceso no puede ver Inbox WhatsApp.' });
            this.router.navigate(['pages', 'access']);
            return;
        }

        const conversationId = id || Number(this.activatedRoute.snapshot.paramMap.get('id') || 0);
        if (!conversationId) {
            return;
        }

        try {
            this.loading = true;
            const response = await this.service.getConversationDetail(conversationId, {
                idEmpresa: this.getRouteCompanyId(),
                markAsRead: true,
            });

            if (!response.ok) {
                this.messageService.add({ severity: 'error', summary: response.msg, detail: response.resp });
                this.conversationDetail = null;
                return;
            }

            this.conversationDetail = this.service.extractConversationDetail(response);
            this.availableReplyTemplates = this.conversationDetail?.available_reply_templates || [];
            await this.loadAssignableUsers();
            await this.loadAvailableReplyTemplates();
            await this.loadFlujoRegistro();
        } catch (error: any) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.message || 'No se pudo cargar la conversación' });
        } finally {
            this.loading = false;
        }
    }

    async loadAssignableUsers(): Promise<void> {
        if (!this.conversation?.id_empresa || !this.canManageAssignment) {
            this.assignableUsers = [];
            return;
        }

        try {
            this.loadingUsers = true;
            const response = await this.service.listAssignableUsers(this.conversation.id_empresa);
            if (!response.ok) {
                this.messageService.add({ severity: 'warn', summary: response.msg, detail: response.resp });
                this.assignableUsers = [];
                return;
            }

            this.assignableUsers = this.service.extractAssignableUsers(response);
        } catch (error: any) {
            this.assignableUsers = [];
            this.messageService.add({ severity: 'warn', summary: 'Usuarios no disponibles', detail: error?.message || 'No se pudo cargar la lista de usuarios' });
        } finally {
            this.loadingUsers = false;
        }
    }

    async refreshConversation(): Promise<void> {
        await this.loadConversation();
    }

    async createContactFromConversation(): Promise<void> {
        if (!this.conversation || !this.canCreateContact) {
            return;
        }

        const phone = this.resolveContactDraftPhone(this.conversation);
        if (!phone) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Número no disponible',
                detail: 'No encontramos un número válido para precargar el contacto.',
            });
            return;
        }

        const draftName = this.resolveContactDraftName(this.conversation);
        const queryParams: Record<string, string | number> = {
            idEmpresa: this.conversation.id_empresa,
            celular: phone,
        };

        if (draftName) {
            queryParams['nombre'] = draftName;
        }

        await this.router.navigate(['pages', 'notificacion', 'contactos', 'nuevo'], {
            queryParams,
        });
    }

    async loadAvailableReplyTemplates(): Promise<void> {
        if (!this.conversation?.id || !this.conversation?.id_empresa) {
            this.availableReplyTemplates = [];
            return;
        }

        if ((this.conversationDetail?.available_reply_templates || []).length > 0) {
            this.availableReplyTemplates = this.conversationDetail?.available_reply_templates || [];
            return;
        }

        try {
            this.loadingTemplates = true;
            const response = await this.service.listAvailableReplyTemplates(this.conversation.id, this.conversation.id_empresa);
            if (!response.ok) {
                this.availableReplyTemplates = [];
                return;
            }

            this.availableReplyTemplates = this.service.extractReplyTemplates(response);
        } catch (error) {
            this.availableReplyTemplates = [];
        } finally {
            this.loadingTemplates = false;
        }
    }

    /**
     * Registro (outcome) del flujo de esta conversación (S5, spec: Inbox
     * Readability). Se pide aparte de `getConversationDetail` porque cuelga
     * de un endpoint distinto (`GET /whatsapp-conversation/:id/flujo`); un
     * fallo acá NO bloquea el resto de la pantalla — la mayoría de las
     * conversaciones no tienen un flujo asociado y eso es un caso normal,
     * no un error.
     */
    async loadFlujoRegistro(): Promise<void> {
        if (!this.conversation?.id || !this.conversation?.id_empresa) {
            this.flujoRegistro = [];
            return;
        }

        try {
            this.loadingFlujoRegistro = true;
            const response = await this.service.obtenerRegistroFlujoDeConversacion(this.conversation.id_empresa, this.conversation.id);
            this.flujoRegistro = this.service.extraerRegistroFlujo(response);
        } catch (error) {
            this.flujoRegistro = [];
        } finally {
            this.loadingFlujoRegistro = false;
        }
    }

    goBack(): void {
        this.router.navigate(['pages', 'notificacion', 'whatsapp-inbox']);
    }

    getStatusLabel(conversation: WhatsappConversationSummary | null): string {
        return getWhatsappConversationUiStatus(conversation).label;
    }

    getStatusSeverity(conversation: WhatsappConversationSummary | null): string {
        return getWhatsappConversationUiStatus(conversation).severity;
    }

    getStatusDetail(conversation: WhatsappConversationSummary | null): string | null {
        return getWhatsappConversationDerivedStatusDetail(conversation);
    }

    private resolveConversationHeadingName(conversation: WhatsappConversationSummary): string {
        const contactName = String(conversation.contact_name || '').trim();
        if (contactName) {
            return contactName;
        }

        const normalizedPhone = String(conversation.customer_phone_normalized || conversation.customer_phone || '').trim();
        const storedName = String(conversation.nombre || '').trim();
        const generatedName = normalizedPhone ? `Conversación con ${normalizedPhone}` : '';

        if (storedName && storedName !== generatedName) {
            return storedName;
        }

        return normalizedPhone || storedName || 'sin identificar';
    }

    private async loadContactCreationAccess(): Promise<void> {
        try {
            this.canCreateContact = await this.service.seguridadService.puedeCrear(
                Contacto.CODIGO_MODULO,
                Contacto.CODIGO_REGISTRO,
            );
        } catch (error) {
            this.canCreateContact = false;
        }
    }

    private hasExistingContact(conversation: WhatsappConversationSummary | null): boolean {
        return Boolean(String(conversation?.contact_name || '').trim());
    }

    private resolveContactDraftPhone(conversation: WhatsappConversationSummary): string {
        return String(conversation.customer_phone_normalized || conversation.customer_phone || '').trim();
    }

    private resolveContactDraftName(conversation: WhatsappConversationSummary): string | null {
        const phone = this.resolveContactDraftPhone(conversation);
        const historialName = String(conversation.historial_nombre || '').trim();
        if (historialName && historialName !== phone) {
            return historialName;
        }

        const headingName = this.resolveConversationHeadingName(conversation);

        if (!headingName || headingName === phone || headingName.toLowerCase() === 'sin identificar') {
            return null;
        }

        return headingName;
    }

    private getRouteCompanyId(): number | undefined {
        const idEmpresa = Number(this.activatedRoute.snapshot.queryParamMap.get('idEmpresa') || 0);
        return idEmpresa > 0 ? idEmpresa : undefined;
    }
}
